<template>
  <div class="main-wrapper">
    <div class="left">
      <retailer-ai-filter
        :is-loaded="loaded"
        :filter-options="filterOptions"
        :selected-filters="selectedFilters"
        :group-by-brand="groupByBrand"
        :products="products"
        @filters-changed="onFiltersChanged"
        @toggle-group-by-brand="toggleGroupByBrand"
      />
    </div>
    <div
      class="right"
      :class="{ 'right-margin': loaded }"
    >
      <h1
        class="title"
        :class="{ 'hide': loaded }"
      >
        What are you looking to buy?
      </h1>
      <retailer-ai-input
        :on-search="handleSearch"
        :loading="loading"
        @success="loaded = true"
        @error="handleError"
      />
      <div
        v-show="(delayedError && !loaded) || (loaded && hasNoProducts)"
        class="error-wrapper"
        :class="{ 'opacity-1': delayedError || (loaded && hasNoProducts) }"
      >
        <p>
          Sorry, I couldn't find products matching your search: "{{ userQuery }}"
        </p>
        <p class="grey">
          Try adjusting your description - e.g., "Show me next season's best-selling women's sneakers under €150" or "Recommend summer dresses from 2026."
        </p>
      </div>
      <p
        v-show="!delayedError"
        class="tag-line"
        :class="{ 'opacity-0': error }"
      >
        <span>✨</span> Use natural language to describe what you're looking for, and let AI find exactly what you should buy.
      </p>
      <p
        v-show="loaded && !hasNoProducts"
        class="response-line"
        :class="{ 'opacity-1': loaded && !hasNoProducts }"
      >
        <strong>You asked to find:</strong> "{{ userQuery }}"
      </p>
      <retailer-ai-products
        v-show="loaded && !hasNoProducts"
        :group-by-brand="groupByBrand"
        :is-products-loading="productsLoading"
      />
    </div>
  </div>
</template>

<script>
import { SEARCH_PRODUCTS } from '@/store/modules/retailer-ai.module'
import {
  createFilterOptions,
  FILTER_PARAM_MAP,
  mergeFilterOptions,
} from '@/views/apps/retailer-ai/utils'
import { debounce } from 'lodash'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import RetailerAiInput from './components/RetailerAiInput.vue'
import RetailerAiProducts from './components/RetailerAiProducts.vue'
import RetailerAiFilter from './components/RetailerAiFilter.vue'

export default {
  name: 'RetailerAI',
  components: {
    RetailerAiInput,
    RetailerAiProducts,
    RetailerAiFilter,
  },
  data() {
    return {
      loaded: false,
      error: false,
      delayedError: false,
      userQuery: '',
      filterOptions: [],
      selectedFilters: {
        'Price, $': ['All'],
      },
      loading: false,
      productsLoading: false,
      groupByBrand: true,
    }
  },
  computed: {
    products() {
      return this.$store.state.retailer.products
    },
    hasNoProducts() {
      const productsList = this.$store.state.retailer?.products || []
      return this.loaded && productsList.length === 0
    },
    transformedFilters() {
      return this.mapFiltersToParams(this.selectedFilters)
    },
  },
  created() {
    this.debouncedSearch = debounce(async query => {
      this.productsLoading = true
      await this.handleSearch(query)
      this.productsLoading = false
    }, 300)

    // Track page view
    analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_VIEWS_AI_PAGE)
  },
  methods: {
    toggleGroupByBrand() {
      this.groupByBrand = !this.groupByBrand

      // Track toggle group by brand
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_TOGGLES_GROUP_BY_BRAND, {
        groupByBrand: this.groupByBrand,
      })
    },

    handleError() {
      this.error = true
      this.loaded = false
      setTimeout(() => {
        this.delayedError = true
      }, 300)

      // Track search failed
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_SEARCH_FAILED, {
        query: this.userQuery,
        error: 'Search error',
      })
    },

    async handleSearch(query) {
      this.error = false
      this.delayedError = false
      this.loading = true
      this.productsLoading = true
      this.userQuery = query

      // Track search query submission
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_SUBMITS_QUERY, {
        query,
      })

      try {
        const response = await this.$store.dispatch(SEARCH_PRODUCTS, {
          params: {
            prompt: query,
            ...this.transformedFilters,
          },
        })

        // Get products and brands from store state rather than response
        const products = response.products || []
        const brands = response.brands || []

        const incomingFilters = createFilterOptions(brands, products)

        let newFilters = incomingFilters.filter(filter => filter.name !== 'Price, $')
        const existingFiltersWithoutPrice = this.filterOptions.filter(filter => filter.name !== 'Price, $')
        newFilters = mergeFilterOptions(existingFiltersWithoutPrice, newFilters)

        this.filterOptions = newFilters
        this.loaded = true

        // Track search success or empty results
        if (products.length === 0) {
          analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_EMPTY_RESULTS, {
            query,
            filters: this.selectedFilters,
          })
        } else {
          analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_SEARCH_SUCCESS, {
            query,
            filters: this.selectedFilters,
            resultCount: products.length,
          })
        }
      } catch (e) {
        this.$emit('error')
      } finally {
        this.loading = false
        this.productsLoading = false
      }
    },
    onFiltersChanged(filters) {
      this.selectedFilters = filters
      this.productsLoading = true

      // Track filter application
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_APPLIES_FILTER, {
        filters: this.selectedFilters,
        query: this.userQuery,
      })

      this.debouncedSearch(this.userQuery, filters)
    },
    mapFiltersToParams(filters) {
      const result = {}

      for (const [key, values] of Object.entries(filters)) {
        if (key === 'wholesale_price_from' || key === 'wholesale_price_to') {
          result[key] = values
          // eslint-disable-next-line no-continue
          continue
        }

        const param = FILTER_PARAM_MAP[key]
        if (param) {
          result[param] = values
        }
      }

      return result
    },
  },
}
</script>
<style scoped lang="scss">
.main-wrapper {
  min-height: calc(100vh - 160px);
  width: 100%;
  display: flex;
  justify-content: center;
  position: relative;
}

.right {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30vh;
  width: 100%;
  transition: margin-top 0.3s ease;
}

.right-margin {
  margin-top: 0;
  margin-left: 20px;
  align-items: flex-start;
}

.right-loaded {
  flex-wrap: wrap;
  width: calc(100% - 305px);
  align-content: flex-start;
}

.title {
  font-size: 36px;
  line-height: 24px;
  text-align: center;
  margin-bottom: 50px;
  opacity: 1;
  display: block;
  transition: all 0.3s ease;
}

.title.hide {
  opacity: 0;
  display: none;
}

.error-wrapper {
  margin-top: 30px;
  font-size: 20px;
  line-height: 24px;
  color: #000;
  width: 71.3%;
  opacity: 0;
  transition: opacity 0.3s ease;

  p {
    margin-bottom: 16px;
    width: 750px;
  }

  .grey {
    color: #757575;
  }
}

.opacity-0 {
  opacity: 0 !important;
}

.opacity-1 {
  opacity: 1 !important;
}

.tag-line {
  span {
    font-size: 24px;
    line-height: 24px;
  }
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  margin-top: 20px;
  color: #757575;
  opacity: 1;
  transition: opacity 0.3s ease;
  width: 971px;
}

.response-line {
  font-size: 20px;
  line-height: 24px;
  text-align: left;
  margin-top: 43px;
  color: #000;
  width: 100%;
}
</style>
